import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Swords, Plus, AlertCircle, HelpCircle, History, ArrowUp, ArrowDown } from 'lucide-react'
import { AnimatePresence } from 'motion/react'
import useBattleStore from '../store/useBattleStore'
import useAppStore from '../../../store/useAppStore'
import BattleCard from '../components/BattleCard'
import GameTabs from '../../../components/GameTabs'
import useAudioManager from '../../../hooks/useAudioManager'
import HelpModal from '../../../components/HelpModal'
import { hapticImpact } from '../../../utils/haptic'
import TeamCapturePreview from '../../team/components/TeamCapturePreview'
import useCaseBattleRealtime from '../hooks/useCaseBattleRealtime'

const BATTLE_PRICE_SORT_STORAGE_KEY = 'battle-price-sort-order'

const Battles = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { battles, games, error, loadBattles, engine, hasSnapshot, isConnected, isModeDisabled } = useBattleStore()
  useCaseBattleRealtime()
  const isInitialLoading = (!hasSnapshot && !error) || (engine === 'worker' && games.length === 0 && !error)
  const canCreate = !isInitialLoading && !isModeDisabled && (engine !== 'node' || isConnected)
  const [showReconnectNotice, setShowReconnectNotice] = useState(false)
  useEffect(() => {
    if (engine !== 'node' || isConnected) {
      setShowReconnectNotice(false)
      return undefined
    }
    const timeout = setTimeout(() => setShowReconnectNotice(true), 3000)
    return () => clearTimeout(timeout)
  }, [engine, isConnected])
  const [priceSortOrder, setPriceSortOrder] = useState(() => {
    if (typeof window === 'undefined') {
      return 'asc'
    }

    const storedSortOrder = window.localStorage.getItem(BATTLE_PRICE_SORT_STORAGE_KEY)
    return storedSortOrder === 'desc' ? 'desc' : 'asc'
  })
  const { playSound } = useAudioManager({
    select: 'select',
    pick: 'pick'
  })
  const [showHelp, setShowHelp] = useState(false)

  // Активная игра: по умолчанию cs2, иначе первая из games[]
  const [activeGame, setActiveGame] = useState(() => {
    return localStorage.getItem('selectedBattleGame') || 'cs2'
  })

  // Сохраняем выбранную игру в localStorage
  useEffect(() => {
    localStorage.setItem('selectedBattleGame', activeGame)
  }, [activeGame])

  // Корректируем активную игру, если её нет в games[]
  useEffect(() => {
    if (games.length > 0 && !games.some(g => g.slug === activeGame)) {
      setActiveGame(games[0].slug)
    }
  }, [games, activeGame])

  const seenBattleIdsRef = useRef(new Set())
  const isInitialSeenSetRef = useRef(false)
  const [animatedBattleIds, setAnimatedBattleIds] = useState(new Set())

  useEffect(() => {
    loadBattles()
  }, [loadBattles, isGuest])

  useEffect(() => {
    if (engine !== 'worker') return undefined
    const interval = setInterval(() => {
      if (!useBattleStore.getState().isLoading) loadBattles()
    }, isGuest ? 10000 : 3000)
    return () => clearInterval(interval)
  }, [engine, isGuest, loadBattles])

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(BATTLE_PRICE_SORT_STORAGE_KEY, priceSortOrder)
  }, [priceSortOrder])

  useEffect(() => {
    if (isInitialLoading) return

    const ids = (battles || [])
      .map(b => (b?.id != null ? String(b.id) : null))
      .filter(Boolean)

    // При первом показе страницы просто помечаем все текущие баттлы как уже виденные
    if (!isInitialSeenSetRef.current) {
      ids.forEach(id => seenBattleIdsRef.current.add(id))
      isInitialSeenSetRef.current = true
      return
    }

    const newIds = ids.filter(id => !seenBattleIdsRef.current.has(id))
    if (newIds.length === 0) return

    // Помечаем новые баттлы для одноразовой анимации
    setAnimatedBattleIds(prev => {
      const next = new Set(prev)
      newIds.forEach(id => next.add(id))
      return next
    })

    newIds.forEach(id => seenBattleIdsRef.current.add(id))

    const timeoutId = setTimeout(() => {
      setAnimatedBattleIds(prev => {
        const next = new Set(prev)
        newIds.forEach(id => next.delete(id))
        return next
      })
    }, 800)

    return () => clearTimeout(timeoutId)
  }, [battles, isInitialLoading])

  const handleCreateBattle = () => {
    if (isGuest) return openLogin()
    playSound('select')
    hapticImpact('medium')
    navigate('/battles/create')
  }

  const sortedBattles = useMemo(() => {
    // Фильтруем баттлы по выбранной игре
    const gameFilteredBattles = battles.filter((battle) => {
      const battleGame = battle?.case?.game || battle?.game
      return battleGame === activeGame
    })

    const getBattlePrice = (battle) => {
      const needBalance = Number(battle?.need_balance)
      if (Number.isFinite(needBalance) && needBalance > 0) {
        return needBalance
      }

      const casePrice = Number(battle?.case?.price) || 0
      const countCase = Number(battle?.count_case) || 1
      return casePrice * countCase
    }

    const getAvailableSlots = (battle) => {
      const currentPlayers = battle?.players?.length || 0
      const totalSlots = parseInt(battle?.count_players, 10) || 0
      return Math.max(totalSlots - currentPlayers, 0)
    }

    const getBattleSortGroup = (battle) => {
      const status = battle?.status
      const hasAvailableSlots = getAvailableSlots(battle) > 0

      if (status === 'waiting' && hasAvailableSlots) return 0
      if (status === 'waiting') return 1
      if (status === 'raffle') return 2
      if (status === 'finished') return 3

      return 4
    }

    return [...gameFilteredBattles].sort((a, b) => {
      // 1) Сначала сортируем по доступности вступления и статусу баттла
      const sortGroupDiff = getBattleSortGroup(a) - getBattleSortGroup(b)
      if (sortGroupDiff !== 0) {
        return sortGroupDiff
      }

      const aPrice = getBattlePrice(a)
      const bPrice = getBattlePrice(b)

      // 2) Внутри группы сортируем по цене (дешевые/дорогие)
      const priceDiff = priceSortOrder === 'asc' ? aPrice - bPrice : bPrice - aPrice
      if (priceDiff !== 0) {
        return priceDiff
      }

      // 3) Внутри одинаковой цены выше те, где меньше свободных слотов
      return getAvailableSlots(a) - getAvailableSlots(b)
    })
  }, [battles, priceSortOrder, activeGame])

  return (
    <div className="relative">
      {/* Background Image - показываем только если баттлов нет */}
      {!isInitialLoading && !error && sortedBattles.length === 0 && (
        <>
          <div 
            className="fixed inset-0 bg-cover bg-top bg-no-repeat"
            style={{ backgroundImage: 'url(/images/case-battle-bg.png)' }}
          />
          
          {/* Dark Overlay */}
          <div className="fixed inset-0 bg-dark-primary/50" />
        </>
      )}
      
      {/* Content */}
      <div className="relative container mx-auto px-4 py-6 pb-12 max-w-2xl lg:px-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 lg:mb-12">
        <div className="flex items-center gap-3">
          <Swords className="w-6 h-6 text-accent-primary lg:w-8 lg:h-8" />
          <h1 className="text-2xl font-bold text-accent-primary lg:text-4xl uppercase italic">
            {t('battle.title', 'Кейс-Баттлы')}
          </h1>
          <button
            onClick={() => setShowHelp(true)}
            className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
          >
            <HelpCircle className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              playSound('select')
              hapticImpact('light')
              setPriceSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            }}
            className="group relative flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 rounded-xl border border-accent-primary/25 bg-gradient-to-b from-dark-secondary/90 to-dark-secondary/60 hover:border-accent-primary/50 hover:shadow-[0_0_20px_rgba(0,255,127,0.22)] transition-all duration-200"
          >
            {priceSortOrder === 'asc' ? (
              <ArrowUp className="w-5 h-5 lg:w-6 lg:h-6 text-accent-primary/90 group-hover:text-accent-primary transition-colors" />
            ) : (
              <ArrowDown className="w-5 h-5 lg:w-6 lg:h-6 text-accent-primary/90 group-hover:text-accent-primary transition-colors" />
            )}
          </button>

          <button
            onClick={handleCreateBattle}
            disabled={!canCreate}
            className="flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-xl font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            <Plus className="w-5 h-5 lg:w-6 lg:h-6" />
          </button>
        </div>
      </div>

      {showReconnectNotice && (
        <div role="status" className="mb-4 rounded-xl border border-info/20 bg-info/10 px-3 py-2 text-sm text-text-secondary">
          {t('battle.socket.reconnecting')}
        </div>
      )}

      {/* Переключатель игр */}
      {games.length > 0 && (
        <GameTabs
          games={games}
          activeGame={activeGame}
          onSelect={(slug) => {
            playSound('pick')
            setActiveGame(slug)
          }}
        />
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {isModeDisabled && <p className="mb-4 text-red-400">{t('battle.socket.mode_disabled')}</p>}

      {/* Battles List */}
      {isInitialLoading ? (
        <div role="status" aria-label={t('common.loading', 'Загрузка')} className="space-y-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="rounded-2xl border border-white/10 bg-surface p-4 motion-safe:animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 shrink-0 rounded-xl bg-white/10" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 w-2/3 rounded bg-white/10" />
                  <div className="h-3 w-1/2 rounded bg-white/10" />
                </div>
              </div>
              <div className="mt-5 h-10 rounded-lg bg-white/10" />
            </div>
          ))}
        </div>
      ) : sortedBattles.length === 0 && !error ? (
        <div className="text-center pt-12">
          <Swords className="w-16 h-16 text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary text-lg mb-4">
            {t('battle.emptyState.title')}
          </p>
          <button
            onClick={handleCreateBattle}
            disabled={!canCreate}
            className="px-6 py-3 bg-accent-primary text-dark-primary rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {t('battle.createFirst')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence initial={false}>
            {sortedBattles.map((battle) => (
                <BattleCard
                  key={battle.id}
                  battle={battle}
                  shouldAnimate={animatedBattleIds.has(String(battle.id))}
                />
              ))}
          </AnimatePresence>
        </div>
      )}

      {/* Ссылка на историю баттлов */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={() => {
            playSound('select')
            hapticImpact('light')
            navigate('/battles/history')
          }}
          className="flex items-center gap-2 text-text-secondary transition-colors"
        >
          <History className="w-4 h-4" />
          <span className="text-sm">{t('battle.viewHistory', 'История баттлов')}</span>
        </button>
      </div>

      <TeamCapturePreview mode="casebattle" className="mt-6" />

      </div>

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        title={t('battle.help.title')}
        steps={[
          {
            title: t('battle.help.step1Title'),
            description: t('battle.help.step1Desc')
          },
          {
            title: t('battle.help.step2Title'),
            description: t('battle.help.step2Desc')
          },
          {
            title: t('battle.help.step3Title'),
            description: t('battle.help.step3Desc')
          },
          {
            title: t('battle.help.step4Title'),
            description: t('battle.help.step4Desc')
          }
        ]}
      />
    </div>
  )
}

export default Battles
